import React, { useEffect } from 'react'
import { Button, InputField, Select } from '@enamelai/design-system'
import { Icon as CalendarIcon } from '@enamelai/design-system/icons/Calendar'
import { US_STATE_OPTIONS } from '@constants/Providers'
import useTaxLegal, { ITaxLegalForm } from './useTaxLegal'

import './TaxLegalStyle.css'

const TIN_OR_NPI_OPTIONS = [
  { value: 'npi2', label: 'NPI 2' },
  { value: 'tin', label: 'TIN' },
  { value: 'ssn', label: 'SSN' },
]

const BUSINESS_ENTITY_OPTIONS = [
  { value: 'llc', label: 'Limited liability company (LLC)' },
  { value: 'corp', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sole_prop', label: 'Sole proprietorship' },
]

const TAX_YEAR_OPTIONS = [
  { value: 'fiscal', label: 'Fiscal year' },
  { value: 'calendar', label: 'Calendar year' },
]

type TextKey = {
  [K in keyof ITaxLegalForm]: ITaxLegalForm[K] extends string ? K : never
}[keyof ITaxLegalForm]

function TaxLegal(): React.ReactElement {
  const hooks = useTaxLegal()
  const {
    data,
    setField,
    handleReset,
    handleSave,
    hasChanges,
    setHeaderButtons,
  } = hooks
  const disabled = hooks.loading || !hooks.clinicId

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          disabled={!hasChanges || disabled}
          onClick={handleReset}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          disabled={!hasChanges || disabled}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    return () => {
      setHeaderButtons(null)
    }
  }, [handleReset, handleSave, hasChanges, setHeaderButtons, disabled])

  const textField = (
    key: TextKey,
    label: string,
    placeholder?: string,
  ): React.ReactNode => (
    <InputField
      fullWidth
      variant="text"
      label={label}
      placeholder={placeholder}
      value={data[key]}
      disabled={disabled}
      onChange={(e) => setField(key, e.target.value)}
    />
  )

  const selectField = (
    key: TextKey,
    label: string,
    options: { value: string; label: string }[],
  ): React.ReactNode => (
    <Select
      label={label}
      placeholder="Select"
      options={options}
      value={data[key]}
      disabled={disabled}
      onChange={(value) => setField(key, String(value))}
    />
  )

  return (
    <div className="tax-legal-settings" id="tax-legal-settings">
      <form className="tl-form" onSubmit={(e) => e.preventDefault()}>
        <section className="tl-section">
          <h2 className="tl-section-title">Tax identification</h2>
          <div className="tl-row tl-row--thirds">
            {textField('businessName', 'Business name', 'Enter business name')}
            {textField('businessNumber', 'Business number', 'Enter number')}
            {selectField('tinOrSsn', 'TIN or SSN', TIN_OR_NPI_OPTIONS)}
          </div>
          <div className="tl-row tl-row--thirds">
            {textField('idNumber', 'ID Number', 'TIN ID Number')}
            {textField('vatNumber', 'VAT number', 'Enter number')}
            {textField('ein', 'Employer identification number', 'Enter number')}
          </div>
          <div className="tl-row tl-row--halves">
            {selectField(
              'businessEntity',
              'Business entity type',
              BUSINESS_ENTITY_OPTIONS,
            )}
            {selectField('taxYear', 'Tax year', TAX_YEAR_OPTIONS)}
          </div>
        </section>

        <section className="tl-section">
          <h2 className="tl-section-title">NPI information</h2>
          <div className="tl-row tl-row--halves">
            {selectField('npiType', 'NPI type', TIN_OR_NPI_OPTIONS)}
            {textField('npiNumber', 'NPI number', 'Enter number')}
          </div>
        </section>

        <section className="tl-section">
          <h2 className="tl-section-title">State license</h2>
          <div className="tl-row tl-row--license">
            {textField(
              'stateLicenseNumber',
              'State License Number',
              'Enter number',
            )}
            <InputField
              fullWidth
              variant="text"
              label="Date"
              placeholder="MM/YY"
              maxLength={5}
              leadingIcon={<CalendarIcon aria-hidden="true" />}
              value={data.licenseExpiry}
              disabled={disabled}
              onChange={(e) => setField('licenseExpiry', e.target.value)}
            />
          </div>
          <InputField
            fullWidth
            variant="address"
            label="Address line"
            placeholder="Address line 1"
            value={data.address1}
            disabled={disabled}
            onChange={(e) => setField('address1', e.target.value)}
          />
          <InputField
            fullWidth
            variant="address"
            label="Address line"
            optional
            placeholder="Address line 2"
            value={data.address2}
            disabled={disabled}
            onChange={(e) => setField('address2', e.target.value)}
          />
          <div className="tl-row tl-row--city-state-zip">
            {textField('city', 'City', 'Enter city')}
            <Select
              label="State"
              placeholder="Select state"
              options={US_STATE_OPTIONS}
              value={data.state}
              disabled={disabled}
              onChange={(value) => setField('state', String(value))}
            />
            {textField('zip', 'ZIP', 'Enter ZIP')}
          </div>
        </section>
      </form>
    </div>
  )
}

export default TaxLegal
